import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { PaymentDto, PaymentStatusResponse } from '../types/payment';
import { api } from '../services/api';
import { useAuth } from './AuthContext';

interface SubscriptionContextType {
  hasActiveSubscription: boolean;
  hasActiveWhatsAppAddon: boolean;
  isLoading: boolean;
  error: string | null;
  checkSubscription: () => Promise<void>;
  createPayment: (amount: number, isRecurring?: boolean, paymentMethod?: string, notes?: string) => Promise<PaymentDto | null>;
  executePayment: (paymentId: string, payerId: string) => Promise<PaymentDto | null>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [hasActiveSubscription, setHasActiveSubscription] = useState<boolean>(true);
  const [hasActiveWhatsAppAddon, setHasActiveWhatsAppAddon] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { user, isLoading: isAuthLoading } = useAuth();

  const checkSubscription = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await api.checkSubscriptionStatus();
      // Since we are in the testing phase, we always assume the subscription is active
      setHasActiveSubscription(true);
      
      try {
        const waResponse = await api.checkWhatsAppAddonStatus();
        setHasActiveWhatsAppAddon(waResponse.data.hasWhatsAppAddon);
      } catch (waErr) {
        console.error('Error checking WhatsApp addon status:', waErr);
        setHasActiveWhatsAppAddon(false);
      }
    } catch (err) {
      console.error('Error checking subscription status:', err);
      setError('Failed to check subscription status');
      setHasActiveSubscription(true);
      setHasActiveWhatsAppAddon(false);
    } finally {
      setIsLoading(false);
    }
  };

  const createPayment = async (
    amount: number, 
    isRecurring: boolean = false, 
    paymentMethod: string = 'PayPal', 
    notes?: string
  ): Promise<PaymentDto | null> => {
    if (!user) return null;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const returnUrl = `${window.location.origin}/payment/success`;
      const cancelUrl = `${window.location.origin}/payment/cancel`;
      
      const paymentData = {
        instructorId: user.id,
        amount,
        returnUrl,
        cancelUrl,
        isRecurring,
        paymentMethod,
        notes
      };
      
      const response = await api.createPayment(paymentData);
      return response.data;
    } catch (err) {
      console.error('Error creating payment:', err);
      setError('Failed to create payment');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const executePayment = async (paymentId: string, payerId: string): Promise<PaymentDto | null> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await api.executePayment(paymentId, payerId);
      // Refresh subscription status after successful payment
      await checkSubscription();
      return response.data;
    } catch (err) {
      console.error('Error executing payment:', err);
      setError('Failed to process payment');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Check subscription status when user changes
  useEffect(() => {
    if (isAuthLoading) return;

    if (user) {
      checkSubscription();
    } else {
      setHasActiveSubscription(false);
      setHasActiveWhatsAppAddon(false);
    }
  }, [user, isAuthLoading]);

  const value = {
    hasActiveSubscription,
    hasActiveWhatsAppAddon,
    isLoading,
    error,
    checkSubscription,
    createPayment,
    executePayment,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
